import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/lib/AuthContext";
import { ModalShell, Field, TextInput, ModalFooter, ErrorBanner } from "../../shared/FormElements";

function fmt(n) {
  if (n === null || n === undefined) return "-";
  return `₹${Number(n).toLocaleString("en-IN")}`;
}

export default function Financials() {
  const { profile } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLog, setShowLog] = useState(false);

  useEffect(() => {
    load();
  }, []);

  async function load() {
    setLoading(true);
    const { data } = await supabase.from("financial_entries").select("*").order("month", { ascending: false });
    setEntries(data || []);
    setLoading(false);
  }

  const current = entries[0];
  const previous = entries[1];

  const computed = useMemo(() => {
    if (!current) return null;
    const growth = previous?.revenue
      ? Math.round(((current.revenue - previous.revenue) / previous.revenue) * 100)
      : null;
    const grossMargin = current.revenue ? Math.round((current.gross_profit / current.revenue) * 100) : null;
    const netMargin = current.revenue ? Math.round((current.net_profit / current.revenue) * 100) : null;
    const runwayMonths = current.operating_expenses
      ? (current.cash_balance / current.operating_expenses).toFixed(1)
      : null;
    return { growth, grossMargin, netMargin, runwayMonths };
  }, [current, previous]);

  const last6 = [...entries].slice(0, 6).reverse();
  const maxRevenue = Math.max(...last6.map((e) => e.revenue || 0), 1);

  if (loading) return <p className="text-sm text-muted-foreground">Loading...</p>;

  return (
    <div>
      <h1 className="text-xl font-semibold text-foreground mb-1">Financials</h1>
      <p className="text-sm text-muted-foreground mb-5">Revenue and financial health, manual monthly entry</p>

      <button
        onClick={() => setShowLog(true)}
        className="bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold mb-5"
      >
        + Log this month
      </button>

      {!current ? (
        <p className="text-sm text-muted-foreground">No entries yet, log your first month above.</p>
      ) : (
        <>
          <div className="grid grid-cols-4 gap-4 mb-5">
            <Kpi label="Monthly revenue" value={fmt(current.revenue)} sub={computed.growth !== null ? `${computed.growth >= 0 ? "+" : ""}${computed.growth}% vs last month` : null} />
            <Kpi label="Revenue target" value={fmt(current.revenue_target)} sub={current.revenue_target ? `${Math.round((current.revenue / current.revenue_target) * 100)}% of target` : null} />
            <Kpi label="Gross margin" value={computed.grossMargin !== null ? `${computed.grossMargin}%` : "-"} />
            <Kpi label="Net profit margin" value={computed.netMargin !== null ? `${computed.netMargin}%` : "-"} />
          </div>

          <div className="grid grid-cols-[1.3fr_1fr] gap-5 mb-5 items-start">
            <div className="border border-border rounded-2xl bg-card overflow-hidden">
              <div className="px-5 py-3.5 border-b border-border font-semibold text-sm text-foreground">
                Revenue vs target, last {last6.length} months
              </div>
              <div className="p-5">
                {last6.map((e) => (
                  <div key={e.month} className="flex items-center gap-3 mb-2.5 last:mb-0">
                    <div className="w-16 text-xs text-muted-foreground">{e.month.slice(0, 7)}</div>
                    <div className="flex-1 h-2 bg-accent rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full"
                        style={{ width: `${Math.min(100, Math.round(((e.revenue || 0) / maxRevenue) * 100))}%` }}
                      />
                    </div>
                    <div className="w-20 text-right font-mono text-xs text-muted-foreground">{fmt(e.revenue)}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="border border-border rounded-2xl bg-card p-5">
              <div className="font-semibold text-sm text-foreground mb-1">Cash runway</div>
              <div className="text-2xl font-semibold text-foreground mb-1">
                {computed.runwayMonths || "-"} <span className="text-sm font-normal text-muted-foreground">months</span>
              </div>
              <p className="text-xs text-muted-foreground mb-4">Cash balance divided by operating expenses</p>
              <Row label="Cash balance" value={fmt(current.cash_balance)} />
              <Row label="Operating expenses" value={fmt(current.operating_expenses)} />
              <Row label="Accounts receivable" value={fmt(current.accounts_receivable)} />
              <Row label="Overdue payments" value={fmt(current.overdue_payments)} highlight />
            </div>
          </div>

          <div className="border border-border rounded-2xl bg-card overflow-hidden">
            <div className="px-5 py-3.5 border-b border-border font-semibold text-sm text-foreground">Monthly log</div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <Th>Month</Th>
                  <Th align="right">Revenue</Th>
                  <Th align="right">Gross profit</Th>
                  <Th align="right">Net profit</Th>
                  <Th align="right">Opex</Th>
                  <Th align="right">Cash balance</Th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.month} className="border-b border-border last:border-0">
                    <td className="px-5 py-3">{e.month.slice(0, 7)}</td>
                    <td className="px-5 py-3 text-right">{fmt(e.revenue)}</td>
                    <td className="px-5 py-3 text-right">{fmt(e.gross_profit)}</td>
                    <td className="px-5 py-3 text-right">{fmt(e.net_profit)}</td>
                    <td className="px-5 py-3 text-right">{fmt(e.operating_expenses)}</td>
                    <td className="px-5 py-3 text-right">{fmt(e.cash_balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {showLog && (
        <LogMonthModal
          adminId={profile?.id}
          existingMonths={entries.map((e) => e.month)}
          onClose={() => setShowLog(false)}
          onSaved={() => {
            setShowLog(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function Kpi({ label, value, sub }) {
  return (
    <div className="border border-border rounded-2xl bg-card p-4">
      <div className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5">{label}</div>
      <div className="text-lg font-semibold text-foreground">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

function Row({ label, value, highlight }) {
  return (
    <div className="flex justify-between text-sm py-1.5 border-b border-border last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className={highlight ? "text-destructive font-medium" : "text-foreground"}>{value}</span>
    </div>
  );
}

function Th({ children, align }) {
  return (
    <th className={`text-${align || "left"} font-mono text-[10.5px] uppercase tracking-wide text-muted-foreground px-5 py-3`}>
      {children}
    </th>
  );
}

function LogMonthModal({ adminId, existingMonths, onClose, onSaved }) {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7) + "-01");
  const [fields, setFields] = useState({
    revenue: "",
    revenue_target: "",
    gross_profit: "",
    operating_expenses: "",
    net_profit: "",
    accounts_receivable: "",
    overdue_payments: "",
    cash_balance: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  function update(key, value) {
    setFields((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSave() {
    if (existingMonths.includes(month)) {
      setError("That month is already logged, edit it directly in the table for now.");
      return;
    }
    setSaving(true);
    setError(null);
    const payload = { month, entered_by: adminId };
    Object.entries(fields).forEach(([k, v]) => {
      payload[k] = v === "" ? null : parseFloat(v);
    });
    const { error: insertError } = await supabase.from("financial_entries").insert(payload);
    setSaving(false);
    if (insertError) {
      setError(insertError.message);
      return;
    }
    onSaved();
  }

  const labels = {
    revenue: "Monthly revenue",
    revenue_target: "Revenue target",
    gross_profit: "Gross profit",
    operating_expenses: "Operating expenses",
    net_profit: "Net profit",
    accounts_receivable: "Accounts receivable",
    overdue_payments: "Overdue payments",
    cash_balance: "Cash balance",
  };

  return (
    <ModalShell title="Log this month" onClose={onClose} wide>
      <ErrorBanner error={error} />
      <Field label="Month">
        <TextInput type="date" value={month} onChange={(e) => setMonth(e.target.value)} />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        {Object.keys(labels).map((key) => (
          <Field key={key} label={labels[key]}>
            <TextInput type="number" value={fields[key]} onChange={(e) => update(key, e.target.value)} />
          </Field>
        ))}
      </div>
      <ModalFooter onClose={onClose} onSave={handleSave} saving={saving} />
    </ModalShell>
  );
}
